/**
 * LoginPage.jsx — Updated with:
 * - Google Sign-In button
 * - Microsoft Sign-In button
 * - Link to forgot password
 * - Email verification banner for unverified users
 * - Register sends verification email automatically
 *
 * Replace your existing LoginPage.jsx with this.
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const T = {
  bg:'#07080c', bg2:'#0c0f18', surf:'#161c2a',
  border:'rgba(255,255,255,.07)', borderH:'rgba(255,255,255,.13)',
  white:'#dce6fa', muted:'rgba(255,255,255,0.50)', dim:'rgba(255,255,255,0.50)',
  cyan:'#00c8f0', blue:'#4d8fff', green:'#2ecc71', red:'#e74c3c',
  yellow:'#f1c40f',
  sans:'"Syne","DM Sans",system-ui,sans-serif',
};

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const inputStyle = {
  width:'100%', padding:'11px 14px',
  background:T.bg2, border:`1px solid ${T.border}`,
  borderRadius:8, color:T.white,
  fontSize:14, fontFamily:T.sans, outline:'none',
  transition:'border-color .15s', boxSizing:'border-box',
};

export default function LoginPage() {
  const [mode,     setMode]     = useState('login');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [name,     setName]     = useState('');
  const [error,    setError]    = useState('');
  const [info,     setInfo]     = useState('');
  const [loading,  setLoading]  = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [mounted,  setMounted]  = useState(false);

  const { login, isAuthenticated, apiFetch, refreshSubscription } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from = location.state?.from || '/dashboard';

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace:true });
    setTimeout(() => setMounted(true), 60);
  }, [isAuthenticated]);

  // ── Email/password submit ────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setInfo(''); setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        navigate(from, { replace:true });
      } else {
        if (!name.trim()) {
          setError('Please enter your name.');
          setLoading(false);
          return;
        }
        const res  = await fetch(`${API}/api/auth/register/`, {
          method:'POST',
          headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ email, password, name: name.trim() }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Registration failed');
        await login(email, password);
        navigate(from, { replace:true });
      }
    } catch(err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Google Sign-In ───────────────────────────────────────────────
const handleGoogle = () => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) {
    setError('Google Sign-In is not configured.');
    return;
  }

  // Open Google OAuth popup
  const width  = 500;
  const height = 600;
  const left   = window.screenX + (window.outerWidth  - width)  / 2;
  const top    = window.screenY + (window.outerHeight - height) / 2;
  const params = new URLSearchParams({
    client_id:     clientId,
    redirect_uri:  `${window.location.origin}/auth/google/callback`,
    response_type: 'token id_token',
    scope:         'openid email profile',
    nonce:         Math.random().toString(36).slice(2),
  });

  const popup = window.open(
    `https://accounts.google.com/o/oauth2/v2/auth?${params}`,
    'google-signin',
    `width=${width},height=${height},left=${left},top=${top}`
  );

  // Listen for the callback
  const timer = setInterval(() => {
    try {
      if (!popup || popup.closed) {
        clearInterval(timer);
        return;
      }
      const url = popup.location.href;
      if (url.includes('/auth/google/callback')) {
        clearInterval(timer);
        popup.close();

        // Extract id_token from the hash fragment
        const hash     = new URL(url).hash.slice(1);
        const fragment = Object.fromEntries(new URLSearchParams(hash));
        const idToken  = fragment.id_token;

        if (idToken) {
          handleGoogleToken(idToken);
        } else {
          setError('Google sign-in failed — no token returned.');
        }
      }
    } catch {
      // Cross-origin error while popup is on google.com — ignore
    }
  }, 200);
};

const handleGoogleToken = async (idToken) => {
  setLoading(true);
  setError('');
  try {
    const res  = await fetch(`${API}/api/auth/social/google/`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ id_token: idToken }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Google sign-in failed');
    localStorage.setItem('access_token',  data.access);
    localStorage.setItem('refresh_token', data.refresh);
    window.location.href = from;
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

  window._handleGoogleCredential = async (response) => {
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API}/api/auth/social/google/`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ id_token: response.credential }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Google sign-in failed');
      // Store tokens and redirect
      localStorage.setItem('access_token',  data.access);
      localStorage.setItem('refresh_token', data.refresh);
      window.location.href = from;
    } catch(err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Microsoft Sign-In ────────────────────────────────────────────
  const handleMicrosoft = async () => {
    setLoading(true); setError('');
    try {
      const { PublicClientApplication } = await import('@azure/msal-browser');
      const msalConfig = {
        auth: {
          clientId:    process.env.REACT_APP_MICROSOFT_CLIENT_ID || '',
          authority:   'https://login.microsoftonline.com/common',
          redirectUri: window.location.origin,
        }
      };
      const msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();

      const result = await msalInstance.loginPopup({
        scopes: ['openid', 'profile', 'email', 'User.Read'],
      });

      const res  = await fetch(`${API}/api/auth/social/microsoft/`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ access_token: result.accessToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Microsoft sign-in failed');

      localStorage.setItem('access_token',  data.access);
      localStorage.setItem('refresh_token', data.refresh);
      window.location.href = from;
    } catch(err) {
      if (err.errorCode !== 'user_cancelled') {
        setError(err.message || 'Microsoft sign-in failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        input::placeholder { color:${T.dim}; }
        input:focus { border-color:rgba(0,200,240,.5) !important; outline:none; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
        /* Google GSI button override */
        #google-btn-container iframe { border-radius:8px !important; }
      `}</style>

      {/* Google GSI script */}
      <script
        src="https://accounts.google.com/gsi/client"
        async defer
        onLoad={() => {
          if (window.google) {
            window.google.accounts.id.initialize({
              client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
              callback:  window._handleGoogleCredential,
              auto_select: false,
            });
          }
        }}
      />

      <div style={{
        minHeight:'100vh', background:T.bg,
        display:'flex', alignItems:'center', justifyContent:'center',
        padding:'24px', position:'relative', overflow:'hidden',
        fontFamily:T.sans,
      }}>
        {/* Background grid */}
        <div style={{
          position:'absolute', inset:0, pointerEvents:'none',
          backgroundImage:`linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)`,
          backgroundSize:'60px 60px',
        }}/>

        <div style={{
          width:'100%', maxWidth:440, position:'relative', zIndex:1,
          opacity: mounted?1:0, transform: mounted?'translateY(0)':'translateY(24px)',
          transition:'opacity .5s ease, transform .5s ease',
        }}>
          {/* Logo */}
          <div style={{ textAlign:'center', marginBottom:28 }}>
            <Link to="/" style={{ textDecoration:'none' }}>
              <div style={{ fontSize:22, fontWeight:900, color:T.white, letterSpacing:'-.02em' }}>
                Tylent<span style={{ color:T.cyan }}>ed</span>
                {/* AI Tools Feature coming soon :) */}
              </div>
            </Link>
          </div>

          {/* Card */}
          <div style={{
            background:'rgba(255,255,255,.025)', border:`1px solid ${T.border}`,
            borderRadius:16, padding:'26px 26px', backdropFilter:'blur(8px)',
          }}>
            {/* Mode toggle */}
            <div style={{
              display:'flex', background:T.bg2,
              border:`1px solid ${T.border}`, borderRadius:8, padding:4, marginBottom:22,
            }}>
              {['login','register'].map(m => (
                <button key={m} onClick={() => { setMode(m); setError(''); setInfo(''); }} style={{
                  flex:1, padding:'8px', borderRadius:6, border:'none',
                  background: mode===m ? T.surf : 'transparent',
                  color: mode===m ? T.white : T.muted,
                  fontSize:13.5, fontWeight: mode===m ? 600 : 400,
                  fontFamily:T.sans, cursor:'pointer', transition:'all .15s',
                }}>
                  {m === 'login' ? 'Sign In' : 'Create Account'}
                </button>
              ))}
            </div>

            {/* Social buttons */}
            <div style={{ display:'flex', gap:8, marginBottom:18 }}>
              {/* Google */}
              <button onClick={handleGoogle} disabled={loading} style={{
                flex:1, padding:'10px 12px', borderRadius:8,
                border:`1px solid ${T.border}`, background:T.bg2,
                color:T.white, fontSize:13, fontFamily:T.sans,
                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:7,
                transition:'all .15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor=T.borderH}
              onMouseLeave={e => e.currentTarget.style.borderColor=T.border}
              >
                <svg width="16" height="16" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.2l6.8-6.8C35.8 2.4 30.3 0 24 0 14.8 0 6.9 5.4 3.1 13.3l7.9 6.1C13 13.3 18 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.9 24.5c0-1.6-.1-3.1-.4-4.5H24v9h13c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8C43.5 38 46.9 31.8 46.9 24.5z"/>
                  <path fill="#FBBC05" d="M11 28.4C10.4 26.8 10 25 10 23s.4-3.8 1-5.4l-7.9-6.1C1.1 14.7 0 18.7 0 23s1.1 8.3 3.1 11.7l7.9-6.3z"/>
                  <path fill="#34A853" d="M24 46c6.3 0 11.6-2.1 15.5-5.7l-7.5-5.8c-2.1 1.4-4.8 2.2-8 2.2-6 0-11-3.8-12.9-9.3l-7.9 6.3C6.9 40.6 14.8 46 24 46z"/>
                </svg>
                Google
              </button>

              {/* Microsoft */}
              {/* <button onClick={handleMicrosoft} disabled={loading} style={{
                flex:1, padding:'10px 12px', borderRadius:8,
                border:`1px solid ${T.border}`, background:T.bg2,
                color:T.white, fontSize:13, fontFamily:T.sans,
                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:7,
                transition:'all .15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor=T.borderH}
              onMouseLeave={e => e.currentTarget.style.borderColor=T.border}
              >
                <svg width="16" height="16" viewBox="0 0 23 23">
                  <path fill="#f35325" d="M1 1h10v10H1z"/>
                  <path fill="#81bc06" d="M12 1h10v10H12z"/>
                  <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                  <path fill="#ffba08" d="M12 12h10v10H12z"/>
                </svg>
                Microsoft
              </button> */}
            </div>

            {/* Divider */}
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:18 }}>
              <div style={{ flex:1, height:1, background:T.border }}/>
              <span style={{ fontSize:12, color:T.dim }}>or continue with email</span>
              <div style={{ flex:1, height:1, background:T.border }}/>
            </div>

            {/* Email form */}
            <form onSubmit={handleSubmit}>
              {mode === 'register' && (
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:12, color:T.muted, display:'block', marginBottom:5 }}>Full Name</label>
                  <input type="text" value={name} onChange={e=>setName(e.target.value)}
                    placeholder="Rohit Jain" required style={inputStyle}/>
                </div>
              )}
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:12, color:T.muted, display:'block', marginBottom:5 }}>Email</label>
                <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                  placeholder="you@example.com" required style={inputStyle}/>
              </div>
              <div style={{ marginBottom: mode==='login' ? 4 : 16 }}>
                <label style={{ fontSize:12, color:T.muted, display:'block', marginBottom:5 }}>Password</label>
                <div style={{ position:'relative' }}>
                  <input type={showPass?'text':'password'} value={password} onChange={e=>setPassword(e.target.value)}
                    placeholder="••••••••" required style={{ ...inputStyle, paddingRight:44 }}/>
                  <button type="button" onClick={()=>setShowPass(p=>!p)} style={{
                    position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
                    background:'none', border:'none', color:T.muted, cursor:'pointer', fontSize:14,
                  }}>{showPass ? '🙈' : '👁'}</button>
                </div>
              </div>

              {mode === 'login' && (
                <div style={{ textAlign:'right', marginBottom:16 }}>
                  <Link to="/forgot-password" style={{ fontSize:12, color:T.cyan, textDecoration:'none' }}>
                    Forgot password?
                  </Link>
                </div>
              )}

              {error && (
                <div style={{
                  padding:'9px 13px', marginBottom:12, borderRadius:7,
                  background:`${T.red}12`, border:`1px solid ${T.red}40`,
                  fontSize:12.5, color:T.red,
                }}>{error}</div>
              )}

              {info && (
                <div style={{
                  padding:'9px 13px', marginBottom:12, borderRadius:7,
                  background:`${T.green}12`, border:`1px solid ${T.green}40`,
                  fontSize:12.5, color:T.green,
                }}>{info}</div>
              )}

              <button type="submit" disabled={loading} style={{
                width:'100%', padding:'12px', border:'none', borderRadius:8,
                background: loading ? `${T.cyan}40` : `linear-gradient(135deg,${T.cyan},${T.blue})`,
                color:'#07080c', fontSize:14, fontWeight:700, fontFamily:T.sans,
                cursor: loading ? 'default' : 'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:8,
              }}>
                {loading && <div style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(0,0,0,.3)', borderTopColor:'#07080c', animation:'spin .65s linear infinite' }}/>}
                {loading ? (mode==='login' ? 'Signing in…' : 'Creating account…') : (mode==='login' ? 'Sign In' : 'Create Account')}
              </button>
            </form>

            {mode === 'register' && (
              <p style={{ fontSize:11.5, color:T.dim, textAlign:'center', marginTop:14, lineHeight:1.6 }}>
                By creating an account you agree to our{' '}
                <Link to="/terms" style={{ color:T.muted }}>Terms</Link>{' '}and{' '}
                <Link to="/privacy" style={{ color:T.muted }}>Privacy Policy</Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
